import { getAsset } from '@pelicargo/tenants';
import { modelUtils } from '@pelicargo/utils';
import { useMemo } from 'react';

import { useCustomerTheme } from './useCustomerTheme';

const { resolveCustomerAsset } = modelUtils.customerTheme;

type CustomerAssetSlot = modelUtils.customerTheme.CustomerAssetSlot;

/**
 * Resolves a named asset slot to a ready-to-use `src`, or `undefined` when the tenant has removed
 * the image for that slot.
 *
 * Mirrors the shape of useRequestFormLabels: a memoised wrapper over a pure resolver.
 * `resolveCustomerAsset` stays pure because `getAsset` reads `window.location` and so cannot live
 * in a shared package — this hook is where the two meet.
 */
export const useCustomerAsset = (slot: CustomerAssetSlot): string | undefined => {
  const theme = useCustomerTheme();
  const override = theme?.assets?.[slot];

  return useMemo(() => {
    const resolved = resolveCustomerAsset(slot, override);

    // `undefined` rather than `''`: an `<img src="">` still renders an element, showing alt text or
    // a broken-image glyph, which is not what "remove this image" means. Every caller guards on it.
    if (resolved.kind === 'none') return undefined;

    return resolved.kind === 'external' ? resolved.url : getAsset(resolved.filename);
  }, [slot, override]);
};
